import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import {
  getInvoiceSummary,
  type GetInvoiceSummaryParams,
} from "features/Invoices/services/InvoiceService";
import type { InvoiceSummaryData } from "features/Invoices/types/invoice.types";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type SummaryMode = "all-time" | "month" | "year" | "range";

// function sourceTypeLabel(sourceType: string): string {
//   const asApi = sourceType as InvoiceSourceType;
//   if (asApi in InvoiceSourceTypeLabelMap) {
//     return InvoiceSourceTypeLabelMap[asApi];
//   }
//   const listLabels = InvoiceListSourceTypeParamLabelMap as Record<
//     string,
//     string
//   >;
//   if (sourceType in listLabels) {
//     return listLabels[sourceType];
//   }
//   return sourceType.replace(/_/g, " ");
// }

// function statusLabel(status: string): string {
//   const s = status as InvoiceStatus;
//   if (s in InvoiceStatusMap) {
//     return InvoiceStatusMap[s].label;
//   }
//   return status.replace(/_/g, " ");
// }

export default function InvoiceSummaryWidgets() {
  const now = useMemo(() => new Date(), []);
  const [mode, setMode] = useState<SummaryMode>("all-time");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState<InvoiceSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    const out: number[] = [];
    for (let i = y - 5; i <= y + 2; i += 1) out.push(i);
    return out;
  }, [now]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    let params: GetInvoiceSummaryParams | undefined;
    if (mode === "month") {
      params = { year, month };
    } else if (mode === "year") {
      params = { year };
    } else if (mode === "range") {
      if (!from || !to) {
        setData(null);
        setError("Select both from and to dates for range summary.");
        setLoading(false);
        return;
      }
      params = { from, to };
    } else {
      params = undefined;
    }

    try {
      const res = await getInvoiceSummary(params);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setData(null);
        setError(res.message || "Could not load invoice summary.");
      }
    } catch (err) {
      setData(null);
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not load invoice summary.";
      setError(
        typeof msg === "string" ? msg : "Could not load invoice summary.",
      );
    } finally {
      setLoading(false);
    }
  }, [mode, year, month, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = data?.totals;
  // const statusRows = data?.byStatus ?? [];
  // const sourceRows = data?.bySourceType ?? [];
  const period = data?.period;

  return (
    <div className="mb-4">
      <Row className="g-3 align-items-end mb-3">
        <Col xs="auto">
          <Label className="form-label text-muted small mb-1 d-block">
            Summary period
          </Label>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Input
              type="select"
              bsSize="sm"
              className="w-auto"
              value={mode}
              onChange={(e) => setMode(e.target.value as SummaryMode)}
              aria-label="Summary mode"
            >
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="range">Range</option>
              <option value="all-time">All time</option>
            </Input>
            {mode === "month" ? (
              <>
                <Input
                  type="select"
                  bsSize="sm"
                  className="w-auto"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  aria-label="Month"
                >
                  {MONTH_LABELS.map((label, i) => (
                    <option key={label} value={i + 1}>
                      {label}
                    </option>
                  ))}
                </Input>
                <Input
                  type="select"
                  bsSize="sm"
                  className="w-auto"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  aria-label="Year"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </>
            ) : null}
            {mode === "year" ? (
              <Input
                type="select"
                bsSize="sm"
                className="w-auto"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                aria-label="Year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Input>
            ) : null}
            {mode === "range" ? (
              <>
                <Input
                  type="date"
                  bsSize="sm"
                  className="w-auto"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  aria-label="From date"
                />
                <Input
                  type="date"
                  bsSize="sm"
                  className="w-auto"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  aria-label="To date"
                />
              </>
            ) : null}
          </div>
        </Col>
        <Col className="text-muted small">
          {period?.start && period?.end ? (
            <>
              Invoice window on <span className="fw-medium">issue date</span>{" "}
              from <DateOnly date={period.start} /> to{" "}
              <DateOnly date={period.end} />
            </>
          ) : (
            <span>All-time invoice summary</span>
          )}
        </Col>
      </Row>

      {error ? (
        <Alert color="danger" className="mb-0">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4">
          <Spinner size="sm" />
          <span>Loading summary…</span>
        </div>
      ) : data && totals ? (
        <>
          <Row className="g-3 mb-3">
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Invoices
                  </p>
                  <h4 className="mb-0 tabular-nums">
                    {totals.invoiceCount.toLocaleString("en-IN")}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Finalized
                  </p>
                  <h4 className="mb-0 tabular-nums">
                    {totals.finalizedInvoiceCount.toLocaleString("en-IN")}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Total billed
                  </p>
                  <h4 className="mb-0 text-primary tabular-nums">
                    {formatCurrency(totals.totalBilledAmount, { compact: true })}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <p className="text-muted text-uppercase fw-medium small mb-1">
                    Still owed
                  </p>
                  <h4 className="mb-0 text-warning tabular-nums">
                    {formatCurrency(totals.pendingInvoiceAmountTotal ?? 0, { compact: true })}
                  </h4>
                  {/* <p className="text-muted small mb-0 mt-2">
                    Issued, sent, and partially paid only (same issue date
                    window).
                  </p> */}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* <Row className="g-3">
            <Col lg={6}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <h6 className="text-muted text-uppercase small mb-3">
                    By status
                  </h6>
                  {statusRows.length === 0 ? (
                    <p className="text-muted mb-0 small">
                      No rows for this period.
                    </p>
                  ) : (
                    <div className="table-responsive mb-0">
                      <Table size="sm" className="align-middle mb-0">
                        <thead className="table-light text-muted small">
                          <tr>
                            <th scope="col">Status</th>
                            <th scope="col" className="text-end">
                              Count
                            </th>
                            <th scope="col" className="text-end">
                              Line total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {statusRows.map((r, i) => (
                            <tr key={`${r.status}-${i}`}>
                              <td>{statusLabel(r.status)}</td>
                              <td className="text-end tabular-nums text-muted">
                                {r.invoiceCount.toLocaleString("en-IN")}
                              </td>
                              <td className="text-end tabular-nums fw-medium">
                                {formatCurrency(r.lineTotal)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="border shadow-none h-100">
                <CardBody className="py-3">
                  <h6 className="text-muted text-uppercase small mb-3">
                    By source type
                  </h6>
                  {sourceRows.length === 0 ? (
                    <p className="text-muted mb-0 small">
                      No rows for this period.
                    </p>
                  ) : (
                    <div className="table-responsive mb-0">
                      <Table size="sm" className="align-middle mb-0">
                        <thead className="table-light text-muted small">
                          <tr>
                            <th scope="col">Source</th>
                            <th scope="col" className="text-end">
                              Count
                            </th>
                            <th scope="col" className="text-end">
                              Line total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sourceRows.map((r, i) => (
                            <tr key={`${r.sourceType}-${i}`}>
                              <td>{sourceTypeLabel(r.sourceType)}</td>
                              <td className="text-end tabular-nums text-muted">
                                {r.invoiceCount.toLocaleString("en-IN")}
                              </td>
                              <td className="text-end tabular-nums fw-medium">
                                {formatCurrency(r.lineTotal)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row> */}
        </>
      ) : null}
    </div>
  );
}
